/**
 * Player D session bootstrap. Unlike Player C there is no legacy raw-media
 * fallback: a D response that is not a valid JSON bootstrap is a retryable
 * failure handled by the normal backoff, never played directly.
 *
 * The `/player-c/` path prefix is intentional: both relay profiles share one
 * public base path; the profile lives inside the session, not in the URL.
 */

const RELAY_TOKEN_PATTERN = '[A-Za-z0-9_-]{32}';
const PLAYLIST_PATH = new RegExp(`/player-c/(${RELAY_TOKEN_PATTERN})/index\\.m3u8$`);
const STATUS_PATH = new RegExp(`/player-c/(${RELAY_TOKEN_PATTERN})/status\\.json$`);
const RELAY_STATES = new Set(['waiting', 'starting', 'ready', 'recovering', 'stopped']);
const VIEW_ID_PATTERN = /^[A-Za-z0-9._:-]{1,200}$/;

export const D_SESSION_BOOTSTRAP_TIMEOUT_MS = 10000;

export class DSessionHttpError extends Error {
  constructor(statusCode) {
    super(`Player D session request failed with HTTP ${statusCode}`);
    this.name = 'DSessionHttpError';
    this.statusCode = Number(statusCode) || null;
  }
}

export function buildDSessionRequestUrl(playURL, viewId, {baseUrl, kickKey} = {}) {
  const fallbackBase =
    baseUrl || (typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
  const url = new URL(playURL, fallbackBase);
  url.searchParams.set('_player_d_session', '1');
  if (viewId) url.searchParams.set('_vid', viewId);
  if (kickKey) url.searchParams.set('_kick', kickKey);
  return url.href;
}

function intOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : null;
}

/** Defensive typing for the screens block the bootstrap embeds while waiting. */
export function sanitizeScreensBlock(block) {
  if (!block || typeof block !== 'object') return null;
  const rows = Array.isArray(block.screens) ? block.screens : [];
  const screens = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    if (typeof row.viewId !== 'string' || row.viewId === '' || row.viewId.length > 300) continue;
    if (typeof row.key !== 'string' || row.key === '' || row.key.length > 300) continue;
    screens.push({
      viewId: row.viewId,
      key: row.key,
      channelName:
        typeof row.channelName === 'string' && row.channelName !== ''
          ? row.channelName.slice(0, 200)
          : null,
      variant: typeof row.variant === 'string' ? row.variant.slice(0, 2).toUpperCase() : null,
      startedAt: intOrNull(row.startedAt),
      isCurrent: row.isCurrent === true
    });
  }
  return {
    maxActiveStreams: intOrNull(block.maxActiveStreams),
    activeCount: intOrNull(block.activeCount),
    screens
  };
}

/**
 * Accept only the opaque same-origin relay resources plus the typed extras of
 * the D contract. Unknown extra fields are ignored so the client and the
 * control plane can ship independently.
 */
export function validateDSessionPayload(payload, baseUrl) {
  if (!payload || typeof payload !== 'object') return null;
  try {
    const runtimeBase = new URL(
      baseUrl || (typeof window !== 'undefined' ? window.location.href : 'http://localhost/')
    );
    const playlistUrl = new URL(payload.playlistUrl, runtimeBase);
    const statusUrl = new URL(payload.statusUrl, runtimeBase);
    if (
      playlistUrl.origin !== runtimeBase.origin ||
      statusUrl.origin !== runtimeBase.origin ||
      playlistUrl.search ||
      playlistUrl.hash ||
      statusUrl.search ||
      statusUrl.hash
    ) {
      return null;
    }
    const playlistMatch = playlistUrl.pathname.match(PLAYLIST_PATH);
    const statusMatch = statusUrl.pathname.match(STATUS_PATH);
    if (!playlistMatch || !statusMatch || playlistMatch[1] !== statusMatch[1]) return null;
    return {
      playlistUrl: playlistUrl.href,
      statusUrl: statusUrl.href,
      state: RELAY_STATES.has(payload.state) ? payload.state : 'starting',
      viewId:
        typeof payload.viewId === 'string' && VIEW_ID_PATTERN.test(payload.viewId)
          ? payload.viewId
          : null,
      screens: sanitizeScreensBlock(payload.screens)
    };
  } catch (_) {
    return null;
  }
}

export async function resolveDPlaySession(
  playURL,
  viewId,
  {signal, fetchImpl, baseUrl, kickKey} = {}
) {
  const request = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!request) throw new Error('d-session-fetch-unavailable');
  const requestUrl = buildDSessionRequestUrl(playURL, viewId, {baseUrl, kickKey});
  const response = await request(requestUrl, {
    method: 'GET',
    headers: {Accept: 'application/json'},
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'follow',
    signal
  });
  const contentType = String(response?.headers?.get?.('content-type') || '').toLowerCase();
  if (!response.ok) {
    try {
      await response.body?.cancel?.();
    } catch (_) {
      // Releasing the failed response is best-effort; classification follows.
    }
    throw new DSessionHttpError(response.status);
  }
  if (!contentType.includes('json')) {
    try {
      // Close a possibly unbounded media body before the retry path reopens
      // /play, otherwise one view can temporarily own two upstream slots.
      await response.body?.cancel?.();
    } catch (_) {
      // Best-effort: the backoff retry follows either way.
    }
    throw new Error('d-session-not-json');
  }
  const validated = validateDSessionPayload(await response.json(), baseUrl);
  if (!validated) throw new Error('d-session-invalid-payload');
  return validated;
}
